import {email, helpers, maxValue, minValue, required} from '@vuelidate/validators'
import {hasProperty, setProperty} from 'dot-prop'
import {DateTime} from 'luxon'

import {loadUserData} from './storage'

export function generateModel(schema) {
	const model = {}

	for (const field of schema.fields) {
		setProperty(model, field.path, field.value ?? null)
	}

	const defaultValues = {...model}

	const userData = loadUserData()

	for (const [fieldKey, fieldValue] of Object.entries(userData.fields)) {
		if (!hasProperty(model, fieldKey.replace(/\[\d+\]/, '[0]'))) {
			continue
		}

		setProperty(model, fieldKey, fieldValue)
	}

	return {model, defaultValues}
}

export function generateValidationRules({fields}, t$) {
	const rules = {}

	// add validators for parents
	for (const field of fields.filter(x => !x.key.includes('.'))) {
		rules[field.key] ??= {}

		for (const [ruleKey, ruleValue] of Object.entries(field.rules || {})) {
			rules[field.key] = {
				...rules[field.key],
				...getValidationRule(ruleKey, ruleValue, t$),
			}
		}
	}

	// add validators for children
	for (const field of fields.filter(x => x.key.includes('.'))) {
		const parentKey = field.key.split('.')[0]
		const fieldKey = field.key.split('.').pop()

		rules[parentKey] ??= {}
		rules[parentKey].$each ??= {}
		rules[parentKey].$each[fieldKey] ??= {}

		for (const [ruleKey, ruleValue] of Object.entries(field.rules || {})) {
			rules[parentKey].$each[fieldKey] = {
				...rules[parentKey].$each[fieldKey],
				...getValidationRule(ruleKey, ruleValue, t$),
			}
		}
	}

	// apply children validators with helper.forEach()
	for (const [itemKey, itemRules] of Object.entries(rules).filter(([key, val]) =>
		Object.keys(val).includes('$each')
	)) {
		rules[itemKey].$each = helpers.forEach(itemRules.$each)
	}

	return rules
}

function getValidationRule(key, value, t$) {
	if (value === false) {
		return {}
	}

	switch (key) {
		case 'required':
			return {
				required: helpers.withMessage(t$.validation_error_required, required),
			}
		case 'min':
			return {
				minValueValue: helpers.withMessage(
					t$.validation_error_min.replace('%%VALUE%%', value),
					minValue(value)
				),
			}
		case 'max':
			return {
				maxValueValue: helpers.withMessage(
					t$.validation_error_max.replace('%%VALUE%%', value),
					maxValue(value)
				),
			}
		case 'email':
			return {
				email: helpers.withMessage(t$.validation_error_email, email),
			}
		case 'mustBeTrue':
			return {
				mustBeTrue: helpers.withMessage(
					t$.validation_error_mustBeTrue,
					fieldValue => fieldValue === true
				),
			}
		case 'futureDate':
			return {
				futureDates: helpers.withMessage(
					t$.validation_error_futureDate,
					fieldValue => fieldValue >= DateTime.now().startOf('day')
				),
			}
		case 'futureDates':
			return {
				futureDates: helpers.withMessage(t$.validation_error_futureDates, fieldValue =>
					fieldValue.every(x => x >= DateTime.now().startOf('day'))
				),
			}
		case 'regex':
			return {
				regex: helpers.withMessage(t$.validation_error_regex, fieldValue =>
					new RegExp(value).test(fieldValue)
				),
			}

		default:
			throw new Error(`Unknown validation rule: "${key} - ${value}"`)
	}
}
